import {gql} from '@apollo/client'
import csv from 'csvtojson'
import {isLeft} from 'fp-ts/lib/Either'
import * as t from 'io-ts'
import React, {useEffect, useRef, useState, useMemo} from 'react'
import styled from 'styled-components'
import {Button} from '../../components/library/buttons'
import {
  FileDrop,
  FilePositioner,
  onDropRejectedHandler
} from '../../components/library/form/file-drop'
import {SelectContainer} from '../../components/library/form/select'
import {Loader} from '../../components/loader'
import {useAnalytics} from '../../lib/analytics/analytics'
import {getToken} from '../../lib/api'
import {useAuth} from '../../lib/auth'
import {AuthState} from '../../lib/cognito'
import {getApi} from '../../lib/fetch'
import {s3Upload} from '../../lib/s3'
import {useCreateCsvUploadAfBulkSendMutation} from './generated/csv-input'
import {AF_CSV_MAX_UPLOAD_FILE_SIZE_BYTES} from '../../common/types/constants'
import {
  validateColumns,
  getTargetAttributes,
  ARRAY_SEPARATOR,
  filterUserProps,
  TARGET_PREFIX
} from '../../common/csv-import/af-bulk-send'
import reporter from 'io-ts-reporters'
import {get} from 'object-path-immutable'

interface URLUploadRes {
  data: {
    uploadUrl: string
    filenameKey: string
  }
}

const TableHeaderText = styled.div`
  font-size: ${p => p.theme.fontSize};
  line-height: ${p => p.theme.lineHeightL};
  font-weight: bold;
  margin-right: 20px;
`
const SecondaryText = styled.div`
  margin-top: 5px;
  color: ${p => p.theme.grey50};
  font-size: ${p => p.theme.fontSizeS};
  line-height: ${p => p.theme.lineHeightCompact};
`

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`

const ErrorDiv = styled.div`
  background-color: ${props => props.theme.redLightest};
  border-radius: ${props => props.theme.borderRadius};
  padding: 20px;
`

const TableWrap = styled.div`
  overflow: auto;
  margin-bottom: 30px;
`

const Table = styled.table`
  border-collapse: collapse;
  min-width: 100%;
  margin-bottom: 15px;
  border-radius: ${props => props.theme.borderRadiusL};
  td,
  th {
    border: 2px solid white;
    background-color: ${props => props.theme.grey05};
  }
  td {
    padding: 10px;
  }
  th ${SelectContainer} {
    background: transparent;
  }
  tbody {
    margin-top: 10px;
  }
  thead th:first-child {
    border-top-left-radius: ${props => props.theme.borderRadiusL};
  }
  thead th:last-child {
    border-top-right-radius: ${props => props.theme.borderRadiusL};
  }
  tr:last-child td:first-child {
    border-bottom-left-radius: ${props => props.theme.borderRadiusL};
  }
  tr:last-child td:last-child {
    border-bottom-right-radius: ${props => props.theme.borderRadiusL};
  }
  td.skip {
    background-color: ${props => props.theme.grey02};
    color: ${props => props.theme.grey20};
  }

  td.error {
    background-color: ${props => props.theme.redLighter};
  }
`

const ModalInner = styled.div<{small?: boolean}>`
  display: flex;
  flex-direction: column;
  ${p =>
    !p.small &&
    `
    min-height: 200px;
  `}
`

const UploadedContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const CsvUploadedDescription = styled.div`
  margin-top: 5px;
  color: ${p => p.theme.grey50};
  font-size: ${p => p.theme.fontSizeS};
  line-height: ${p => p.theme.lineHeightCompact};
  flex: 1;
`

export const AFBulkCSVInput = ({
  environment,
  possibleUserFields,
  selectedActionFlowId,
  isTestMode
}: {
  environment: {id: string; organisationId: string}
  possibleUserFields: string[]
  selectedActionFlowId: string | undefined
  isTestMode: boolean | undefined
}) => {
  const [step, setStep] = useState<'file-select' | 'preview' | 'uploaded'>(
    'file-select'
  )
  const analytics = useAnalytics()
  const [errors, setErrors] = useState<string[]>([])

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const activeFile = useRef<File>()
  const xhrRequest = useRef<XMLHttpRequest>()
  const api = getApi({organisationId: environment.organisationId, getToken})
  const [doCreateCsvUploadMutation] = useCreateCsvUploadAfBulkSendMutation({
    onCompleted: () => {
      analytics.track({
        event: 'Initiated Action Flow bulk send file import',
        properties: {
          environment_id: environment.id,
          org_id: environment.organisationId,
          category: 'tools>action-flow-bulk-send'
        }
      })
    }
  })

  useEffect(() => {
    // Clear errors between steps
    setErrors([])
  }, [step])

  const userAuth = useAuth()

  const uploadCsvFile = (uploadUrl: string, file: File) => {
    return s3Upload(uploadUrl, file, (p, xhr) => {
      if (p.lengthComputable) {
        console.log(p.loaded / p.total)
      }
      if (!xhrRequest.current) {
        xhrRequest.current = xhr
      }
    })
  }

  const handleImportError = (importError: string | Error) => {
    console.error('Error uploading the file', importError)
    setLoading(false)
    setErrors(['Unable to upload CSV file'])
  }

  const importRows = async ({
    auth,
    timezone,
    locale,
    columns
  }: {
    auth: AuthState
    timezone?: string | undefined
    locale?: string | undefined
    columns: string[]
  }) => {
    setLoading(true)

    const file = activeFile.current
    if (file) {
      let uploadResp: URLUploadRes

      // Get the singed url to upload and save
      try {
        const resp = await api(
          'post',
          `/workbench/v1/customer/${environment.id}/getCsvUploadUrl`,
          {
            filename: file.name,
            filetype: file.type
          }
        )
        if (!resp.ok) {
          console.error('Error getting upload url', resp.body)
          setLoading(false) // disable the loading
          setErrors(['Unable to upload CSV file']) // show error prompt
          return // stop from closing the modal
        }
        uploadResp = resp.json as URLUploadRes
      } catch (getUrlError) {
        console.error('Error retrieving upload url', getUrlError)
        setLoading(false)
        setErrors(['Unable to upload CSV file'])
        return
      }

      // Upload the file to the CSV
      try {
        const uploadResult = await uploadCsvFile(
          uploadResp.data.uploadUrl,
          file
        )
        if (uploadResult.status >= 400) {
          handleImportError(uploadResult.body)
          return
        }
      } catch (uploadError) {
        handleImportError(uploadError as Error)
        return
      }

      // Create CSV upload task
      try {
        await doCreateCsvUploadMutation({
          variables: {
            environmentId: environment.id,
            organisationId: environment.organisationId,
            filename: file.name,
            filenameKey: uploadResp.data.filenameKey,
            rowCount: rows.length,
            isTest: isTestMode ?? true, // default to true if undefined for safety
            metadata: {
              source: {
                page: {
                  path: location.pathname,
                  search: location.search,
                  title: document.title,
                  url: location.href
                },
                user:
                  auth && auth.authenticated
                    ? {
                        userId: auth.sub,
                        email: auth.email,
                        locale,
                        timezone
                      }
                    : undefined
              },
              filetype: file.type,
              targetActionFlowId: selectedActionFlowId,
              userTargetProperties: getTargetAttributes(columns),
              columns
            }
          }
        })
      } catch (mutationError) {
        console.error(mutationError)
        if (mutationError instanceof Error) {
          setErrors([mutationError.message])
        } else if (typeof mutationError === 'string') {
          setErrors([mutationError])
        } else {
          setErrors(['Unable to start Action Flows'])
        }
        setLoading(false)
        return
      }
    }

    setStep('uploaded')
    setLoading(false)
  }

  const onDropRejected = onDropRejectedHandler(
    (msg: string) => setErrors([msg]),
    AF_CSV_MAX_UPLOAD_FILE_SIZE_BYTES
  )

  const onDrop = (files: File[]) => {
    setLoading(true)
    if (!files.length) {
      setLoading(false)
      return
    }

    setErrors([])

    const file = files[0]
    if (!file) {
      setLoading(false)
      setErrors(['Error reading file, is it a CSV?'])
      return
    }

    const reader = new FileReader()
    activeFile.current = file
    // eslint-disable-next-line functional/immutable-data
    reader.onload = () => {
      csv()
        .fromString(reader.result as string)
        .then(previewRows => {
          // validate the parsed object is record
          const validPreviewRows = t.array(t.UnknownRecord).decode(previewRows)
          if (
            isLeft(validPreviewRows) ||
            Object.keys(validPreviewRows.right).length === 0
          ) {
            console.error(reporter.report(validPreviewRows))
            setErrors(["Sorry that doesn't appear to be a valid csv file"])
            setLoading(false)
            return
          }

          setRows(validPreviewRows.right)
          setStep('preview')
          setLoading(false)
        })
    }
    reader.readAsText(file)
  }

  const columnData = useMemo(() => {
    if (step !== 'preview') {
      return
    }

    const foundColumns = Object.keys(rows[0]).reduce((acc, curr) => {
      // eslint-disable-next-line functional/immutable-data
      if (typeof rows[0][curr] === 'string') {
        // eslint-disable-next-line functional/immutable-data
        acc.push(curr)
      }
      const currentColumn = rows[0][curr]
      if (typeof currentColumn === 'object') {
        const currColumnObj = currentColumn as Record<string, unknown>
        const column = Object.keys(currColumnObj).map(k => {
          if (typeof currColumnObj[k] === 'string') {
            return `${curr}.${k}`
          }
          const nestedColumn = currColumnObj[k]
          if (typeof nestedColumn === 'object') {
            const nestedColumnObject = nestedColumn as Record<string, unknown>
            return Object.keys(nestedColumnObject).map(kk => {
              if (typeof nestedColumnObject[kk] === 'string') {
                return `${curr}.${k}.${kk}`
              }
              setErrors(['Invalid data, only expect two levels of nesting'])
              return ''
            })
          }
          setErrors(['Invalid data, expect only strings or objects'])
          return ''
        })
        // eslint-disable-next-line functional/immutable-data
        acc.push(...column.flat())
      }

      return acc.flat().filter(Boolean)
    }, [] as string[])

    const columnErr = validateColumns(
      foundColumns,
      possibleUserFields.filter(filterUserProps)
    )
    return {columns: foundColumns, columnErrors: columnErr}
  }, [possibleUserFields, step, rows])

  const allErrors = [...errors, ...(columnData?.columnErrors ?? [])]

  if (loading) {
    return <Loader delay={0} />
  }

  return (
    <>
      <ModalInner small={step === 'uploaded'}>
        {step === 'file-select' ? (
          <FilePositioner>
            <FileDrop
              hasPreview={false}
              onDrop={onDrop}
              dropzoneProps={{
                maxSize: AF_CSV_MAX_UPLOAD_FILE_SIZE_BYTES,
                onDropRejected,
                accept: {
                  'text/csv': ['.csv']
                }
              }}
            >
              {allErrors.length ? <Errors errors={allErrors} /> : null}
            </FileDrop>
          </FilePositioner>
        ) : step === 'preview' ? (
          <>
            {allErrors.length ? <Errors errors={allErrors} /> : null}
            {!allErrors.length && rows.length && columnData?.columns.length ? (
              <TableWrap data-testid={'csv-import-table'}>
                <TableHeaderText>Preview</TableHeaderText>
                <SecondaryText>
                  Review data before proceeding, you are targetting users by{' '}
                  {getTargetAttributes(columnData.columns).join(' and ')}
                </SecondaryText>
                <Table>
                  <thead>
                    <tr>
                      {columnData.columns.map((k, i) => {
                        return (
                          <th style={{padding: '10px'}} key={`${k}-${i}`}>
                            {k}
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 9).map((row, i) => (
                      <tr key={i} style={{whiteSpace: 'nowrap'}}>
                        {columnData.columns.map((k, x) => {
                          return (
                            <td key={`${k}-${i}-${x}`}>
                              {k.startsWith(TARGET_PREFIX)
                                ? get(row, k)
                                : get(row, k).replaceAll(ARRAY_SEPARATOR, ', ')}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                    {rows.length > 9 && (
                      <tr key="remaining-rows-tr">
                        <SecondaryText>
                          ... and {(rows.length - 9).toLocaleString()} more row
                          {rows.length - 9 > 1 ? 's' : ''}
                        </SecondaryText>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </TableWrap>
            ) : null}
            <Buttons>
              <Button
                appearance="secondary"
                style={{marginRight: '10px'}}
                onClick={() => {
                  setStep('file-select')
                  // reset state on new upload
                  setErrors([])
                }}
              >
                Cancel
              </Button>
              <Button
                data-testid="button-csv-upload"
                appearance="primary"
                disabled={
                  !columnData?.columns.length ||
                  !!allErrors.length ||
                  !selectedActionFlowId
                }
                onClick={() =>
                  importRows({
                    auth: userAuth.authState,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    locale: navigator.language,
                    columns: columnData?.columns ?? []
                  })
                }
              >
                {`Start ${rows.length.toLocaleString()} Action Flow${
                  rows.length > 1 ? 's' : ''
                }`}
              </Button>
            </Buttons>
          </>
        ) : step === 'uploaded' ? (
          <>
            <UploadedContainer>
              {allErrors.length ? <Errors errors={allErrors} /> : null}
              <CsvUploadedDescription>
                We're processing your import. You can track progress by watching
                the task on the right hand side of the screen. You can navigate
                away from this page and return at any time.
              </CsvUploadedDescription>
            </UploadedContainer>
            <Buttons>
              <Button
                appearance="secondary"
                style={{marginRight: '10px'}}
                onClick={() => {
                  setStep('file-select')
                  setErrors([])
                }}
              >
                Send another
              </Button>
            </Buttons>
          </>
        ) : null}
      </ModalInner>
    </>
  )
}

const Errors = ({errors}: {errors: string[]}) => (
  <ErrorDiv>
    {errors.length > 1 ? errors.map(e => <li key={e}>{e}</li>) : errors}
  </ErrorDiv>
)

gql`
  mutation createCsvUploadAFBulkSend(
    $organisationId: String!
    $environmentId: String!
    $filename: String!
    $filenameKey: String!
    $rowCount: Int!
    $metadata: JSON!
    $isTest: Boolean!
  ) {
    createCsvUploadTask(
      input: {
        csvUploadTask: {
          organisationId: $organisationId
          environmentId: $environmentId
          filename: $filename
          filenameKey: $filenameKey
          status: PENDING
          rowCount: $rowCount
          metadata: $metadata
          taskType: AF_BULK_SEND
          isTest: $isTest
        }
      }
    ) {
      csvUploadTask {
        organisationId
        environmentId
        filename
        filenameKey
        status
        processedRowCount
        processFrom
        processTo
        metadata
        created
        updated
        isTest
      }
    }
  }
`
